/**
 * The Milky Way as numbers: positions and colours for ~230k stars, the dust, and a backdrop of distant stars.
 * Seeded and pure (no Math.random, no three.js), so the picture is the same every visit and can be tested.
 * Units are kiloparsecs; the disc lies in the x-y plane with +z up.
 */

export type Rgb = readonly [number, number, number];

export const PITCH = (24 * Math.PI) / 180;
export const DISC_RADIUS_MAX = 20;
export const BULGE_RADIUS_MAX = 3.5;
export const DEFAULT_SEED = 7;

export const DEFAULT_COUNTS = { disc: 180_000, bar: 22_000, bulge: 30_000, dust: 2_000, backdrop: 1_400 };

/** sRGB-authored palette entries, as the picture was designed; converted to linear radiance for the HDR pipeline. */
const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const lin = (c: readonly [number, number, number]): Rgb => [srgbToLinear(c[0]), srgbToLinear(c[1]), srgbToLinear(c[2])];

export const YELLOW_WHITE: Rgb = lin([1, 0.86, 0.62]);
export const BLUE_WHITE: Rgb = lin([0.5, 0.68, 1.0]);
export const PINK: Rgb = lin([1, 0.4, 0.5]);
export const WARM: Rgb = lin([1, 0.68, 0.38]);

/** Small, fast, good-enough seeded generator returning [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Standard normal by Box-Muller. */
export function gaussian(rng: () => number): number {
  const u = Math.max(rng(), 1e-9), v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Exponential-ish disc radius, r = -3.2 ln(u1 u2): a gamma(2) profile with scale length 3.2 kpc. */
export function discRadius(rng: () => number): number {
  return -3.2 * Math.log(Math.max(rng(), 1e-9) * Math.max(rng(), 1e-9));
}

/** How strongly (0..1) the point (r, theta) sits on one of the two log-spiral arms; the centre is faded out. */
export function armWeight(r: number, theta: number): number {
  const armPhase = Math.log(Math.max(r, 0.3) / 0.3) / Math.tan(PITCH);
  let d = (((theta - armPhase) % Math.PI) + Math.PI) % Math.PI; // nearest arm, mod pi: two arms
  d = Math.min(d, Math.PI - d);
  return Math.exp(-(d / 0.3) * (d / 0.3)) * (1 - Math.exp(-r / 2.5));
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export interface GalaxyCloud {
  /** xyz per point */
  positions: Float32Array;
  /** linear rgb per point */
  colors: Float32Array;
  /** points in the disc, bar and bulge, in that order in the arrays */
  discCount: number;
  barCount: number;
  bulgeCount: number;
}

/** Disc + bar + bulge as one cloud, so it draws in a single call. */
export function generateGalaxy(seed: number = DEFAULT_SEED, counts: { disc: number; bar: number; bulge: number } = DEFAULT_COUNTS): GalaxyCloud {
  const rng = mulberry32(seed);
  const total = counts.disc + counts.bar + counts.bulge;
  const positions = new Float32Array(total * 3);
  const colors = new Float32Array(total * 3);
  let n = 0;

  // the disc: exponential radial profile, two spiral arms, thin and flaring
  let attempts = 0;
  const maxAttempts = counts.disc * 80 + 1000;
  while (n < counts.disc && attempts++ < maxAttempts) {
    const r = discRadius(rng);
    if (r > DISC_RADIUS_MAX) continue;
    const th = rng() * Math.PI * 2;
    const w = armWeight(r, th);
    if (rng() > 0.22 + 0.78 * w) continue;
    const z = (rng() - 0.5) * 2 * (0.25 * (0.6 + r / 20)) * (rng() < 0.5 ? 1 : 0.5);
    let c: Rgb = [lerp(YELLOW_WHITE[0], BLUE_WHITE[0], w), lerp(YELLOW_WHITE[1], BLUE_WHITE[1], w), lerp(YELLOW_WHITE[2], BLUE_WHITE[2], w)];
    let lum = Math.exp(-0.3 + gaussian(rng));
    if (rng() < 0.035 * w) { c = PINK; lum *= 3; } // the rare hot pink star-forming knot, three times brighter
    lum = Math.min(12, Math.max(0.05, lum));
    const k = Math.min(1, 0.18 + 0.12 * Math.sqrt(lum));
    positions[3 * n] = r * Math.cos(th); positions[3 * n + 1] = r * Math.sin(th); positions[3 * n + 2] = z;
    colors[3 * n] = c[0] * k; colors[3 * n + 1] = c[1] * k; colors[3 * n + 2] = c[2] * k;
    n++;
  }
  const discCount = n;

  // the bar: an elongated gaussian, tilted 25 degrees from the x axis
  const ba = (25 * Math.PI) / 180, ca = Math.cos(ba), sa = Math.sin(ba);
  for (let i = 0; i < counts.bar; i++, n++) {
    const bx = gaussian(rng) * 1.6, by = gaussian(rng) * 0.55, bz = gaussian(rng) * 0.45;
    positions[3 * n] = bx * ca - by * sa; positions[3 * n + 1] = bx * sa + by * ca; positions[3 * n + 2] = bz;
    const k = 0.16 + 0.1 * rng();
    colors[3 * n] = WARM[0] * k; colors[3 * n + 1] = WARM[1] * k; colors[3 * n + 2] = WARM[2] * k;
  }

  // the bulge: a Hernquist profile, flattened a little
  for (let i = 0; i < counts.bulge; i++, n++) {
    const u = 0.02 + rng() * 0.965, s = Math.sqrt(u);
    const raw = (0.45 * s) / (1 - s);
    // the profile's far tail is cut at 3.5 kpc; spread the cut points over the outer 40% so they do not form a visible shell
    const rb = raw < BULGE_RADIUS_MAX ? raw : BULGE_RADIUS_MAX * (0.6 + 0.4 * rng());
    let x = gaussian(rng), y = gaussian(rng), zz = gaussian(rng);
    const len = Math.hypot(x, y, zz) || 1; x /= len; y /= len; zz /= len;
    positions[3 * n] = rb * x; positions[3 * n + 1] = rb * y; positions[3 * n + 2] = rb * zz * 0.7;
    const k = 0.14 + 0.1 * rng();
    colors[3 * n] = WARM[0] * k; colors[3 * n + 1] = WARM[1] * k; colors[3 * n + 2] = WARM[2] * k;
  }

  return { positions, colors, discCount, barCount: counts.bar, bulgeCount: counts.bulge };
}

export interface PointSet { positions: Float32Array; colors: Float32Array; count: number; }

/** Large dim dust sprites hugging the arms. Colours are pre-scaled to 0.08 so a few thousand overlap into a haze. */
export function generateDust(seed: number = DEFAULT_SEED + 1, n: number = DEFAULT_COUNTS.dust): PointSet {
  const rng = mulberry32(seed);
  const positions = new Float32Array(n * 3), colors = new Float32Array(n * 3);
  let i = 0, attempts = 0;
  while (i < n && attempts++ < n * 400 + 1000) {
    const r = discRadius(rng);
    if (r > DISC_RADIUS_MAX) continue;
    const th = rng() * Math.PI * 2;
    const w = armWeight(r, th);
    if (rng() > w) continue; // dust lanes follow the arms closely
    const z = (rng() - 0.5) * 2 * (0.25 * (0.6 + r / 20)) * 0.5;
    positions[3 * i] = r * Math.cos(th); positions[3 * i + 1] = r * Math.sin(th); positions[3 * i + 2] = z;
    colors[3 * i] = lerp(YELLOW_WHITE[0], BLUE_WHITE[0], w) * 0.08;
    colors[3 * i + 1] = lerp(YELLOW_WHITE[1], BLUE_WHITE[1], w) * 0.08;
    colors[3 * i + 2] = lerp(YELLOW_WHITE[2], BLUE_WHITE[2], w) * 0.08;
    i++;
  }
  return { positions, colors, count: i };
}

/** Distant stars on a big sphere, so the dark has something in it. */
export function generateBackdrop(seed: number = DEFAULT_SEED + 2, n: number = DEFAULT_COUNTS.backdrop, radius = 900): PointSet {
  const rng = mulberry32(seed);
  const positions = new Float32Array(n * 3), colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    let x = gaussian(rng), y = gaussian(rng), z = gaussian(rng);
    const len = Math.hypot(x, y, z) || 1; x = (x / len) * radius; y = (y / len) * radius; z = (z / len) * radius;
    positions[3 * i] = x; positions[3 * i + 1] = y; positions[3 * i + 2] = z;
    const q = rng(), b = 0.04 + 0.26 * q * q; // mostly faint, a few bright
    colors[3 * i] = b * 0.85; colors[3 * i + 1] = b * 0.9; colors[3 * i + 2] = b;
  }
  return { positions, colors, count: n };
}
